/**
 * Frido Axis Priced-Rows runtime — one placement renders ONE priced-row block
 * for the option axis named by the `axis` prop: rows are option values priced
 * from variants; picking swaps the variant in place. Combo offers are a
 * different snippet (fcbfrd03) — no combo/group mode here.
 *
 * Row decoration priority: authored placement props (`media` value→URL,
 * `badges` value→label — rendered as the tinted merchandising chip), then the
 * per-value variant image, then bundle-slot tiers matched by title (black
 * offer chip / FREE-COUPON — pool.bundle is decoration only; tier CARDS are
 * fcbfrd01). card_style 'hero' renders image-bearing rows as stacked hero
 * cards (maternity bundle look). No detection anywhere.
 */
;(() => {
  if (window.__sai_fcbfrd02_initialized__) return
  window.__sai_fcbfrd02_initialized__ = true

  const SNIPPET_ID = 'fcbfrd02'
  const TAG = 'sai-fcbfrd02'
  const FEATURE_SLUG = 'variant_picker'
  // Cap over-fetched cover image URLs at a DPR3 target (Shopify CDN only). CAP-ONLY:
  // an already-smaller width param is left untouched, so pre-sized covers stay byte-identical.
  const coverCdn = (u, w) => { if (!u || typeof u !== 'string') return u; if (!/\/cdn\/shop\/|cdn\.shopify\.com/.test(u)) return u; const m = /[?&]width=(\d+)/.exec(u); if (m) { return (+m[1] <= w) ? u : u.replace(/([?&])width=\d+/, '$1width=' + w); } return u + (u.includes('?') ? '&' : '?') + 'width=' + w; }


  function esc(s) {
    return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
  }
  // per-variant badge construct (value_display badges: { label, bg, fg, icon }) — the
  // variant-LEVEL source, same one the swatch/option snippets render. Fully
  // inline-styled so it ships with the HTML (not held back by the theme's
  // compiled {% stylesheet %} cache). Sits above the row like the offer chip.
  function badgePill(b) {
    if (!b || !b.label) return ''
    const bg = b.bg || '#000000'
    const fg = b.fg || '#ffffff'
    const icon = b.icon
      ? (/^(https?:)?\/\//.test(b.icon)
          ? `<img src="${esc(b.icon)}" alt="" loading="lazy" decoding="async" style="width:12px;height:12px;object-fit:contain;display:block">`
          : `<span aria-hidden="true">${esc(b.icon)}</span>`)
      : ''
    return `<span style="display:inline-flex;align-items:center;gap:3px;padding:4px 8px;border-radius:6px;font-size:11px;font-weight:500;line-height:1;white-space:nowrap;background:${esc(bg)};color:${esc(fg)}">${icon}${esc(b.label)}</span>`
  }
  function badgesHtml(list) {
    return Array.isArray(list) && list.length
      ? `<div style="align-self:flex-start;margin-left:12px;margin-bottom:-10px;z-index:2;position:relative;display:inline-flex;gap:4px">${list.map(badgePill).join('')}</div>`
      : ''
  }
  function readPool(node) {
    for (let i = 0; i < node.children.length; i++) {
      const c = node.children[i]
      if (c.tagName === 'SCRIPT' && c.hasAttribute('data-spectrum-snippet-pool')) {
        try { return JSON.parse(c.textContent) } catch (e) { return {} }
      }
    }
    return {}
  }
  function fmtMoney(major, cur) {
    if (major == null || Number.isNaN(Number(major))) return ''
    return (cur || '₹') + Math.round(Number(major)).toLocaleString('en-IN')
  }
  function timerState(key, minutes) {
    const total = minutes * 60 * 1000
    let start = 0
    try { start = Number(localStorage.getItem(key)) || 0 } catch (e) {}
    const now = Date.now()
    if (!start || now - start >= total) {
      start = now
      try { localStorage.setItem(key, String(start)) } catch (e) {}
    }
    return { remaining: total - (now - start), total }
  }
  function fmtClock(ms) {
    const s = Math.max(0, Math.floor(ms / 1000))
    const h = String(Math.floor(s / 3600)).padStart(2, '0')
    const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0')
    const sec = String(s % 60).padStart(2, '0')
    return `${h}<small>h</small>&nbsp; ${m}<small>m</small> ${sec}<small>s</small>`
  }
  function helpSvg() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 1 1 3.4 2.34c-.7.27-1.4.83-1.4 1.66v.5"/><circle cx="11.9" cy="16.6" r=".5" fill="currentColor"/></svg>'
  }
  function arrowSvg() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="13 6 19 12 13 18"/></svg>'
  }

  const COUPON_RE = /^(free\s*)?coupon/i

  // tier.badges[] → { badge: black chip text, coupon: chip text } split
  function splitBadges(badges) {
    const out = { badge: '', coupon: '' }
    for (const b of badges || []) {
      const label = (b && b.label) || ''
      if (!label) continue
      if (COUPON_RE.test(label.replace(/^free\s*/i, '')) || /^free/i.test(label)) {
        out.coupon = label
      } else if (!out.badge) {
        out.badge = label
      }
    }
    return out
  }

  function buildRows(pool, curVariant) {
    if (!pool.axis || !pool.axis.values || !pool.axis.values.length) return []
    const props = pool.props || {}
    // Per-value image / badge / subtitle all arrive as DATA on each pool value
    // (image ← spectrum.value_media, badges ← spectrum.value_badges, subtitle
    // ← spectrum.value_subtitles) — never authored props.
    // fallback decoration: bundle-slot tiers matched by title — the same
    // variant-decoration mechanic the live store uses for volume axes
    const decoTiers = (pool.bundle && pool.bundle.reward && pool.bundle.reward.tiers) || []
    const axisKey = 'option' + (pool.axis.position || 1)
    const allVs = (pool.product && pool.product.variants) || []
    return pool.axis.values.map((v, i) => {
      const deco = decoTiers.find((t) => (t.title || '').toLowerCase() === String(v.v).toLowerCase())
      const { badge, coupon } = splitBadges(deco ? deco.badges : [])
      // Cross-axis price: a row's price depends on the FULL Category+Size+this-value combo,
      // not just this value — so find the variant {this value, current other axes} and price
      // from it. The server-baked pool value (`v.price`) is only the "from" (MIN across all
      // sibling combos); using it verbatim freezes the price when a sibling axis changes.
      // Fall back to the pool value when there is no current variant yet.
      let price = v.price
      let compareAt = v.compare_at
      if (curVariant && allVs.length) {
        const others = ['option1', 'option2', 'option3'].filter((k) => k !== axisKey)
        const match = allVs.find((vv) => vv[axisKey] === v.v && others.every((k) => curVariant[k] == null || vv[k] === curVariant[k]))
        if (match) { price = match.price; compareAt = match.compare_at }
      }
      return {
        idx: i, value: v.v,
        title: v.v,
        price: price != null ? price / 100 : null,
        mrp: compareAt && compareAt > price ? compareAt / 100 : null,
        save: compareAt && compareAt > price ? (compareAt - price) / 100 : null,
        image: v.image || null,
        subtitle: v.subtitle || '',
        timerMin: Number(v.timer) || 0,
        // default_value prop overrides the current-variant preselection
        selected: props.default_value ? v.v === props.default_value : v.v === pool.axis.selected,
        badge,
        variantBadges: v.badges,
        coupon,
      }
    })
  }



  const El = class extends HTMLElement {
    connectedCallback() {
      // A sibling axis (Category / Size) changing the variant re-prices every row: each
      // value's price depends on the full Category+Size+value combo, so recompute from
      // product.variants for the new selection + re-render. Bound outside the _init guard so
      // a re-mounted element re-subscribes; our own picks set _curVariantId first so an event
      // we fired is a no-op here.
      if (!this._onVariantChange) {
        this._onVariantChange = (e) => {
          const id = e && e.detail && e.detail.variantId
          if (id == null || String(id) === String(this._curVariantId) || !this._pool || !this._pool.axis) return
          this._curVariantId = id
          this._rows = buildRows(this._pool, this._currentVariant())
          this._render()
        }
      }
      window.addEventListener('spectrum:variant-change', this._onVariantChange)
      if (this._init) return
      this._init = true
      this._track = () => {}
      this._timers = []
    }
    disconnectedCallback() {
      if (this._onVariantChange) window.removeEventListener('spectrum:variant-change', this._onVariantChange)
      this._clearTimers()
    }
    setAnalytics(t) { this._track = typeof t === 'function' ? t : () => {} }
    _clearTimers() { for (const t of this._timers) clearInterval(t); this._timers = [] }

    applyVariant(_content, pool) {
      this._pool = pool && typeof pool === 'object' ? pool : {}
      if (this._curVariantId == null && this._pool.product) this._curVariantId = this._pool.product.current_variant_id
      this._rows = buildRows(this._pool, this._currentVariant())
      if (!this._rows.length) return
      let syncInitTier = false
      if (this._selected == null) {
        const bySel = this._rows.findIndex((r) => r.selected)
        this._selected = bySel >= 0 ? bySel : 0
        const initRow = this._rows[this._selected]
        // A default_value prop preselects a tier whose variant can differ from
        // the product's current/default variant. buildRows highlights that tier,
        // but the ATC form + ?variant still point at the default variant — so
        // add-to-cart submits the default variant while the UI shows the
        // preselected tier. Sync the form to the preselected tier on first paint,
        // guarded to the mismatch case so an instance whose preselected tier
        // already matches the current variant never rewrites ?variant on load.
        syncInitTier = !!(initRow && this._pool.axis && initRow.value !== this._pool.axis.selected)
      }
      this._render()
      this._bind()
      if (syncInitTier) this._applyAxisValue(this._rows[this._selected].value)
    }

    _currentVariant() {
      const prod = this._pool && this._pool.product
      if (!prod) return null
      let id = this._curVariantId
      if (id == null) {
        // the ATC form input is the shared truth the sibling pickers keep synced
        const inp = document.querySelector('form[action*="/cart/add"] input[name="id"]')
        id = (inp && inp.value) || prod.current_variant_id
      }
      return (prod.variants || []).find((v) => String(v.id) === String(id)) || null
    }

    _props() { return (this._pool && this._pool.props) || {} }

    _label() {
      const p = this._props()
      if (p.label_prefix) return p.label_prefix
      if (this._pool.axis && this._pool.axis.name) return this._pool.axis.name.toUpperCase() + ' :'
      return 'OPTIONS :'
    }

    _timerGateOff() {
      // Placement kill-switch only. WHICH row ticks (and for how long) is
      // authored DATA — value_display.timer minutes per option value — never a
      // product-level metafield. A row with no `timer` simply shows no clock.
      return this._props().show_timer === false
    }

    _render() {
      const props = this._props()
      const cur = this._pool.product.currency
      const selRow = this._rows[this._selected]
      const label = `<div class="sai-fcbfrd02__label"><span class="sai-fcbfrd02__label-key">${esc(this._label())}</span><span class="sai-fcbfrd02__label-val" data-sel-label>${esc(selRow ? selRow.title : '')}</span>${lmTriggerHTML(this._pool && this._pool.learn_more)}</div>`
      const timerGateOff = this._timerGateOff()

      let rows = '<div class="sai-fcbfrd02__rows">'
      for (let i = 0; i < this._rows.length; i++) {
        const r = this._rows[i]
        const selected = i === this._selected
        // variant-level badge (value_display badges) — the proper source; renders the
        // authored pill (colour + icon). The offer/authored text chip below is a
        // separate, tier-level concern.
        const vbadge = badgesHtml(r.variantBadges)
        const badge = r.badge
          ? `<div class="sai-fcbfrd02__badge">${esc(r.badge).replace(/(\d+% OFF)/i, '<b>$1</b>')}</div>` : ''
        // Figma: SAVE shows on volume tiers and the hero card, not on
        // side-by-side product rows. The hero card is the badged offer row,
        // else the non-current image row.
        const heroMode = props.card_style === 'hero'
        if (heroMode && this._heroIdx == null) {
          const byBadge = this._rows.findIndex((x) => x.badge && x.image)
          const byImage = this._rows.findIndex((x) => x.image && !x.current)
          this._heroIdx = byBadge >= 0 ? byBadge : byImage
        }
        const isHeroRow = heroMode && i === this._heroIdx && !!r.image
        const showSave = props.show_save !== false
        const save = r.save && showSave ? `<span class="sai-fcbfrd02__save">${props.save_label || 'SAVE'} ${fmtMoney(r.save, cur)}</span>` : ''
        const mrp = r.mrp && r.mrp > r.price ? `<span class="sai-fcbfrd02__mrp">${props.mrp_label || 'MRP'} <s>${fmtMoney(r.mrp, cur)}</s></span>` : ''
        let coupon = ''
        if (r.coupon) {
          const main = r.coupon.replace(/^free\s*/i, '')
          const m = main.match(/^(.*?)(₹?[\d,]+)?\s*$/)
          coupon = `<span class="sai-fcbfrd02__plus">+</span><span class="sai-fcbfrd02__coupon"><span class="sai-fcbfrd02__coupon-free">FREE</span><span class="sai-fcbfrd02__coupon-main"><span class="lbl">${esc((m && m[1] || main).trim())}</span>${m && m[2] ? `<span class="amt">${esc(m[2])}</span>` : ''}</span></span>`
        }
        // countdown renders on the default/selected tier when timers are on
        const rowTimerMin = timerGateOff ? 0 : r.timerMin
        const showTimer = rowTimerMin > 0
        const timer = showTimer
          ? `<div class="sai-fcbfrd02__timer" data-timer-key="sai-frido-timer:${esc(this._pool.product.handle)}:combo:${i}" data-timer-min="${rowTimerMin}"><span class="sai-fcbfrd02__timer-label">${esc(props.timer_label || 'Limited Time Offer')}</span><span class="sai-fcbfrd02__timer-clock" data-clock></span></div>` : ''

        // card_style prop: 'rows' (default, side-by-side thumb) or 'hero'
        // (whole-card variant: big centered image on top, title+prices below,
        // applied to image-bearing non-current rows — the accessory-combo look)
        const hero = isHeroRow
        let rowInner
        if (hero) {
          rowInner = `<div class="sai-fcbfrd02__hero"><img src="${esc(coverCdn(r.image, 360))}" alt="" loading="lazy" decoding="async"></div>
            <div class="sai-fcbfrd02__row-between">
              <span class="sai-fcbfrd02__title">${esc(r.title)}</span>
              <span class="sai-fcbfrd02__row-right">${save}<span class="sai-fcbfrd02__prices">${mrp}<span class="sai-fcbfrd02__price">${fmtMoney(r.price, cur)}</span></span></span>
            </div>`
        } else {
          const thumb = r.image && props.show_thumb !== false && !heroMode ? `<img class="sai-fcbfrd02__row-thumb" src="${esc(coverCdn(r.image, 170))}" alt="" loading="lazy" decoding="async">` : ''
          // optional subtitle lines under the title (data: spectrum.value_subtitles);
          // newlines split into separate lines (first medium, rest light)
          const subLines = r.subtitle ? String(r.subtitle).split('\n').filter(Boolean) : []
          const subtitle = subLines.length
            ? `<span class="sai-fcbfrd02__subtitle">${subLines.map((l, li) => `<span class="${li === 0 ? 'sai-fcbfrd02__sub-main' : 'sai-fcbfrd02__sub-desc'}">${esc(l)}</span>`).join('')}</span>`
            : ''
          const titleBlock = `<span class="sai-fcbfrd02__rowtitle"><span class="sai-fcbfrd02__title${r.image ? ' sai-fcbfrd02__title--sm' : ''}">${esc(r.title)}</span>${subtitle}</span>`
          rowInner = `<span class="sai-fcbfrd02__row-left">${thumb}${titleBlock}${coupon}</span>
            <span class="sai-fcbfrd02__row-right">${save}<span class="sai-fcbfrd02__prices">${mrp}<span class="sai-fcbfrd02__price">${fmtMoney(r.price, cur)}</span></span></span>`
        }

        rows += `<div class="sai-fcbfrd02__unit">${vbadge}${badge}
          <button type="button" class="sai-fcbfrd02__row${selected ? ' is-sel' : ''}${hero ? ' sai-fcbfrd02__row--stacked' : ''}" data-action="pick" data-idx="${i}">${rowInner}</button>${timer}</div>`
      }
      rows += '</div>'

      let help = ''
      if (props.show_need_help === true) {
        help = `<div class="sai-fcbfrd02__help" data-action="help" role="button" tabindex="0">
          <div class="sai-fcbfrd02__help-left">${helpSvg()}<div><div class="sai-fcbfrd02__help-title">${esc(props.need_help_title || 'Need help choosing a variant')}</div><div class="sai-fcbfrd02__help-sub">${esc(props.need_help_subtitle || 'Compare and discover')}</div></div></div>
          <span style="color:var(--f-blue);display:inline-flex">${arrowSvg()}</span>
        </div>`
      }

      this.innerHTML = `<div class="sai-fcbfrd02__inner">${label}${rows}${help}</div>`
      this._startTimers()
    }

    _startTimers() {
      this._clearTimers()
      for (const el of this.querySelectorAll('[data-timer-key]')) {
        const key = el.dataset.timerKey
        const min = Number(el.dataset.timerMin) || 0
        if (!min) continue
        const clock = el.querySelector('[data-clock]')
        const tick = () => { if (clock) clock.innerHTML = fmtClock(timerState(key, min).remaining) }
        tick()
        this._timers.push(setInterval(tick, 1000))
      }
    }

    // combo variant-axis mode: behave like any variant picker (in place)
    _applyAxisValue(value) {
      const axis = this._pool.axis
      if (!axis) return
      const key = 'option' + axis.position
      const allVs = this._pool.product.variants || []
      const vs = allVs.filter((v) => v[key] === value)
      // Preserve the other axes of the variant currently in the ATC form so
      // this pick keeps the shopper's other selections instead of snapping to
      // the first available one.
      const curInput = document.querySelector('form[action*="/cart/add"] input[name="id"]')
      const cur = curInput ? allVs.find((v) => String(v.id) === String(curInput.value)) : null
      let variant = null
      if (cur) {
        const others = ['option1', 'option2', 'option3'].filter((k) => k !== key)
        variant =
          vs.find((v) => v.available && others.every((k) => cur[k] == null || v[k] === cur[k])) ||
          vs.find((v) => others.every((k) => cur[k] == null || v[k] === cur[k])) ||
          null
      }
      if (!variant) variant = vs.find((v) => v.available) || vs[0] || null
      axis.selected = value
      if (variant) {
        // mark first so our own variant-change listener treats this as a no-op
        this._curVariantId = variant.id
        try {
          const url = new URL(window.location.href)
          url.searchParams.set('variant', String(variant.id))
          window.history.replaceState({}, '', url.toString())
        } catch (e) {}
        window.dispatchEvent(new CustomEvent('spectrum:variant-change', { detail: { variantId: variant.id } }))
        // Sync the theme product form's hidden variant input. This snippet
        // replaces a native option picker, and product-form.js submits whatever
        // is in `[name="id"]` — without this, add-to-cart uses the default
        // variant regardless of the picked value. Scope to forms whose current
        // value is one of THIS product's variants so other-product forms are
        // never touched.
        try {
          const _vids = {}
          allVs.forEach((vv) => { _vids[String(vv.id)] = 1 })
          document.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
            if (_vids[String(input.value)]) {
              input.value = String(variant.id)
              input.dispatchEvent(new Event('change', { bubbles: true }))
            }
          })
        } catch (e) {}
      }
    }


    _bind() {
      if (this._clickBound) return
      this._clickBound = true
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-action]')
        if (!t) return
        if (t.dataset.action === 'pick') return this._pick(Number(t.dataset.idx))
        if (t.dataset.action === 'help') {
          window.dispatchEvent(new CustomEvent('spectrum:compare-open', {}))
          this._track(`${FEATURE_SLUG}:need_help_elem_clicked`, {})
        }
      })
    }

    _pick(i) {
      const row = this._rows[i]
      if (!row || i === this._selected) return
      this._selected = i
      this._render()
      this._applyAxisValue(row.value)
      this._track(`${FEATURE_SLUG}:select_combo`, { index: i, title: row.title })
    }
  }


  /* ---- "Learn More" comparison dialog (verified live 2026-08-25) --------
     The SDK re-renders this snippet from its pool JSON and discards the server
     markup (styles, dialog and inline scripts included), so the trigger, the
     dialog and the CSS are all built here instead. Backdrop blur and the
     open/close motion are the Compare Variants dialog's, verbatim; the panel
     dimensions are the source storefront's for this modal (578px wide, 40px
     padding, 24px radius — measured at a 1280px viewport).
  ------------------------------------------------------------------------ */
  const LM_CSS = `.sai-fcvfrd01__dialog:not([open]) { display: none !important; }
    .sai-fcvfrd01__dialog { padding: 0; padding-block: env(safe-area-inset-top) env(safe-area-inset-bottom); box-sizing: border-box; border: none; background: transparent; width: 100vw; max-width: 100vw; height: 100dvh; max-height: 100dvh; margin: 0; overflow: visible; font-family: 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; color: #131313; }
    .sai-fcvfrd01__dialog[open] { display: flex; align-items: flex-end; justify-content: center; }
    .sai-fcvfrd01__dialog::backdrop { background: #0000; backdrop-filter: blur(0px); -webkit-backdrop-filter: blur(0px); transition: background .45s cubic-bezier(.25,0,.3,1), backdrop-filter .45s cubic-bezier(.25,0,.3,1); }
    .sai-fcvfrd01__dialog--open::backdrop { background: #00000073; backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); }
    .sai-fcvfrd01__wrap { position: relative; z-index: 1; width: 100%; max-width: 520px; max-height: 100dvh; display: flex; flex-direction: column; align-items: stretch; justify-content: flex-end; transform: translateY(100%); transition: transform .45s cubic-bezier(.25,0,.3,1); will-change: transform; backface-visibility: hidden; }
    .sai-fcvfrd01__dialog--open .sai-fcvfrd01__wrap { transform: translateY(0); }
    .sai-fcvfrd01__panel { width: 100%; max-height: 92dvh; display: flex; flex-direction: column; padding: 20px; border-radius: 20px 20px 0 0; background: #fff; color: #131313; box-sizing: border-box; box-shadow: 0 -12px 40px #00000029; overflow: hidden; }
    .sai-fcvfrd01__head { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-shrink: 0; padding-bottom: 16px; }
    .sai-fcvfrd01__title { margin: 0; padding: 0; font-size: 22px; font-weight: 700; line-height: 1.2; letter-spacing: -0.01em; color: #000; }
    .sai-fcvfrd01__close { flex: 0 0 auto; width: 32px; height: 32px; padding: 0; border: none; border-radius: 100px; background: transparent; color: #131313; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
    .sai-fcvfrd01__close:focus-visible { outline: 2px solid #131313; outline-offset: 2px; }
    .sai-fcvfrd01__close:hover { background: #f4f4f4; }
    .sai-fcvfrd01__scroll { flex: 1 1 auto; min-height: 0; overflow-x: hidden; overflow-y: auto; -webkit-overflow-scrolling: touch; overscroll-behavior: contain; scrollbar-width: thin; scrollbar-color: #ddd transparent; display: flex; flex-direction: column; gap: 16px; }
    .sai-fcvfrd01__scroll::-webkit-scrollbar { width: 6px; }
    .sai-fcvfrd01__scroll::-webkit-scrollbar-thumb { background: #ddd; border-radius: 4px; }
    .sai-fcvfrd01__image { width: 100%; display: block; border-radius: 12px; overflow: hidden; flex-shrink: 0; }
    .sai-fcvfrd01__image img { display: block; width: 100%; height: auto; }
    .sai-fcvfrd01__image--htm { background: #f7f7f7; }
    .sai-fcvfrd01__htm-card { appearance: none; display: flex; align-items: center; gap: 12px; width: 100%; padding: 12px; border: none; border-radius: 16px; background: linear-gradient(180deg, #ffffff 0%, #dbeafe 100%); color: #131313; font-family: inherit; font-size: 14px; font-weight: 500; line-height: 1.4; text-align: left; cursor: pointer; box-sizing: border-box; flex-shrink: 0; }
    .sai-fcvfrd01__htm-card:focus-visible { outline: 2px solid #307fe2; outline-offset: 2px; }
    .sai-fcvfrd01__htm-thumb { display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; flex-shrink: 0; border-radius: 12px; background: #fff; overflow: hidden; }
    .sai-fcvfrd01__htm-thumb img { width: 100%; height: 100%; object-fit: contain; }
    .sai-fcvfrd01__htm-text { flex: 1; min-width: 0; color: #307fe2; }
    .sai-fcvfrd01__htm-link { color: #307fe2; text-decoration: underline; text-underline-offset: 2px; white-space: nowrap; }
    .sai-fcvfrd01__footer { flex-shrink: 0; padding-top: 16px; display: flex; flex-direction: column; gap: 8px; }
    .sai-fcvfrd01__cta { width: 100%; padding: 16px; border: none; border-radius: 14px; background: #131313; color: #fff; font-family: inherit; font-size: 16px; font-weight: 600; line-height: 1.2; cursor: pointer; transition: opacity .15s ease; flex-shrink: 0; }
    .sai-fcvfrd01__cta:hover { opacity: .92; }
    .sai-fcvfrd01__cta:focus-visible { outline: 2px solid #131313; outline-offset: 2px; }
    .sai-fcvfrd01__htm { position: absolute; inset: 0; display: flex; flex-direction: column; justify-content: flex-end; pointer-events: none; transform: translateY(100%); transition: transform .4s cubic-bezier(.25,0,.3,1); will-change: transform; }
    .sai-fcvfrd01__htm[data-open="true"] { pointer-events: auto; transform: translateY(0); }
    .sai-fcvfrd01__panel--htm { max-height: 92dvh; }
    @media (min-width: 750px) {
      .sai-fcvfrd01__dialog[open] { align-items: center; padding: 24px; }
      .sai-fcvfrd01__wrap { max-width: 420px; opacity: 0; transform: scale(.96); transition: opacity .24s ease, transform .24s ease; }
      .sai-fcvfrd01__dialog--open .sai-fcvfrd01__wrap { opacity: 1; transform: scale(1); }
      .sai-fcvfrd01__panel, .sai-fcvfrd01__panel--htm { border-radius: 16px; max-height: min(92dvh, 700px); padding: 20px; }
      .sai-fcvfrd01__title { font-size: 22px; }
      .sai-fcvfrd01__cta { padding: 14px; font-size: 15px; border-radius: 12px; }
      .sai-fcvfrd01__htm-card { font-size: 13px; padding: 10px; }
      .sai-fcvfrd01__scroll { gap: 12px; }
      .sai-fcvfrd01__head { padding-bottom: 12px; }
      .sai-fcvfrd01__footer { padding-top: 12px; }
      .sai-fcvfrd01__htm { transform: scale(.96); opacity: 0; transition: opacity .24s ease, transform .24s ease; }
      .sai-fcvfrd01__htm[data-open="true"] { opacity: 1; transform: scale(1); }
    }
    @media (prefers-reduced-motion: reduce) {
      .sai-fcvfrd01__dialog::backdrop, .sai-fcvfrd01__wrap, .sai-fcvfrd01__htm { transition: none; }
    }

.sai-fcbfrd02__label { width: 100%; }
.sai-fcbfrd02__learn { appearance: none; background: transparent; border: none; margin-left: auto; padding: 0; color: #307fe2; font: inherit; font-size: 14px; font-weight: 500; line-height: 1.2; text-decoration: underline; text-underline-offset: 3px; white-space: nowrap; cursor: pointer; }
.sai-fcbfrd02__learn:focus-visible { outline: 2px solid #307fe2; outline-offset: 2px; border-radius: 4px; }
[data-sai-fcb-lm] .sai-fcvfrd01__title { font-size: 24px; line-height: 28.8px; font-weight: 600; color: #000; }
[data-sai-fcb-lm] .sai-fcvfrd01__cta { padding: 14px; border-radius: 12px; font-size: 20px; line-height: 24px; font-weight: 600; }
@media (min-width: 750px) {
  [data-sai-fcb-lm] .sai-fcvfrd01__wrap { max-width: 578px; }
  [data-sai-fcb-lm] .sai-fcvfrd01__panel { padding: 40px; border-radius: 24px; max-height: min(92dvh, 760px); }
}
`
  const lmEsc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
  function lmEnsureStyle() {
    if (document.getElementById('sai-fcb-lm-css')) return
    const st = document.createElement('style')
    st.id = 'sai-fcb-lm-css'
    st.textContent = LM_CSS
    document.head.appendChild(st)
  }
  function lmEnsureDialog(lm) {
    let d = document.getElementById(lm.id)
    if (d) return d
    d = document.createElement('dialog')
    d.className = 'sai-fcvfrd01__dialog'
    d.id = lm.id
    d.setAttribute('data-sai-fcv-dialog', '')
    d.setAttribute('data-sai-fcb-lm', '')
    d.setAttribute('aria-labelledby', lm.id + '-title')
    d.innerHTML =
      '<div class="sai-fcvfrd01__wrap"><div class="sai-fcvfrd01__panel">' +
        '<div class="sai-fcvfrd01__head">' +
          '<h2 class="sai-fcvfrd01__title" id="' + lmEsc(lm.id) + '-title">' + lmEsc(lm.title) + '</h2>' +
          '<button type="button" class="sai-fcvfrd01__close" data-sai-fcv-close aria-label="Close">' +
            '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M4 4l10 10M14 4L4 14" stroke="#131313" stroke-width="1.6" stroke-linecap="round"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="sai-fcvfrd01__scroll"><div class="sai-fcvfrd01__image">' +
          '<img src="' + lmEsc(lm.src) + '" srcset="' + lmEsc(lm.src600) + ' 600w, ' + lmEsc(lm.src900) + ' 900w, ' + lmEsc(lm.src) + ' 1200w" ' +
          'sizes="(min-width: 750px) 498px, calc(100vw - 40px)" alt="' + lmEsc(lm.alt) + '" width="' + lmEsc(lm.w) + '" height="' + lmEsc(lm.h) + '" loading="lazy" decoding="async">' +
        '</div></div>' +
        '<div class="sai-fcvfrd01__footer"><button type="button" class="sai-fcvfrd01__cta" data-sai-fcv-close>' + lmEsc(lm.close) + '</button></div>' +
      '</div></div>'
    document.body.appendChild(d)
    return d
  }
  function lmBindOnce() {
    if (window.__saiFcbLmBound) return
    window.__saiFcbLmBound = true
    const OPEN = 'sai-fcvfrd01__dialog--open'
    const closeIt = (d) => {
      if (!d || !d.open) return
      d.classList.remove(OPEN)
      const wrap = d.querySelector('.sai-fcvfrd01__wrap')
      let done = false
      const finalize = () => { if (done) return; done = true; if (d.open) d.close(); document.body.style.overflow = '' }
      if (wrap) wrap.addEventListener('transitionend', function h(e) {
        if (e.propertyName !== 'transform' && e.propertyName !== 'opacity') return
        wrap.removeEventListener('transitionend', h); finalize()
      })
      setTimeout(finalize, 550)
    }
    document.addEventListener('click', (e) => {
      const t = e.target
      if (!(t instanceof Element)) return
      const openBtn = t.closest('[data-sai-fcv-open]')
      if (openBtn) {
        const d = document.getElementById(openBtn.getAttribute('aria-controls'))
        if (d && !d.open) { d.showModal(); document.body.style.overflow = 'hidden'; void d.offsetHeight; d.classList.add(OPEN) }
        return
      }
      const closeBtn = t.closest('[data-sai-fcv-close]')
      if (closeBtn) { closeIt(closeBtn.closest('[data-sai-fcv-dialog]')); return }
      if (e.target instanceof HTMLDialogElement && e.target.matches('[data-sai-fcb-lm]')) {
        const wrap = e.target.querySelector('.sai-fcvfrd01__wrap')
        if (wrap && !wrap.contains(e.target)) closeIt(e.target)
      }
    })
    document.addEventListener('cancel', (e) => {
      const d = e.target
      if (!(d instanceof HTMLDialogElement) || !d.matches('[data-sai-fcb-lm]')) return
      e.preventDefault(); closeIt(d)
    })
  }
  function lmTriggerHTML(lm) {
    if (!lm || !lm.src) return ''
    lmEnsureStyle(); lmEnsureDialog(lm); lmBindOnce()
    return '<button type="button" class="sai-fcbfrd02__learn" data-sai-fcv-open aria-controls="' + lmEsc(lm.id) + '">' + lmEsc(lm.label || 'Learn More') + '</button>'
  }

  if (!customElements.get(TAG)) customElements.define(TAG, El)

  function bindAll() {
    const api = window.__spectrumAi && window.__spectrumAi.snippet
    for (const node of document.querySelectorAll(`[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`)) {
      const el = node.querySelector(TAG)
      if (!el) continue
      // First paint straight from the server-rendered pool — the pool's props
      // are the instance's default config, so rendering never waits on the
      // SDK (which may load after DOMContentLoaded or resolve asynchronously).
      if (!el._painted) {
        const initPool = readPool(node)
        if (initPool && initPool.axis) { el._painted = true; el.applyVariant({}, initPool) }
      }
      if (api && typeof api.bind === 'function' && !el._wrapBound) {
        el._wrapBound = true
        const handles = api.bind(node, (ctx) => {
          const e = node.querySelector(TAG)
          if (e) e.applyVariant(ctx.variant ? ctx.variant.content : {}, readPool(node))
        })
        if (handles) el.setAnalytics(handles.track)
      }
    }
    for (const el of document.querySelectorAll(TAG)) {
      if (el._applied || el._wrapBound || el.closest('[data-spectrum-instance-id]')) continue
      const parent = el.closest('[data-spectrum-lq-snippet]') || el.parentElement
      if (!parent) continue
      const pool = readPool(parent)
      if (pool && pool.axis) { el._applied = true; el.applyVariant({}, pool) }
    }
  }

  if (typeof globalThis !== 'undefined' && globalThis.__SAI_TEST_HARNESS__ === true) {
    globalThis.__saiFcbfrd02 = { buildRows, splitBadges, timerState, fmtClock, fmtMoney }
  }

  function schedule() {
    bindAll()
    // the SDK can load after us — retry until it binds (or give up quietly)
    let tries = 0
    const t = setInterval(() => {
      bindAll()
      const api = window.__spectrumAi && window.__spectrumAi.snippet
      if ((api && typeof api.bind === 'function') || ++tries > 20) clearInterval(t)
    }, 500)
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true })
  } else {
    schedule()
  }

  // The storefront SDK can re-mount slot wrappers after load (conflict
  // resolution / vis flips) — a replaced wrapper holds a fresh unpainted
  // element, so rebind on DOM changes, debounced.
  const __saiRelevant = (records) => {
    for (const r of records) {
      for (const n of r.addedNodes) {
        if (n.nodeType !== 1) continue
        if ((n.matches && n.matches(`${TAG}, [data-spectrum-snippet-id="${SNIPPET_ID}"], [data-spectrum-lq-snippet]`)) ||
            (n.querySelector && n.querySelector(`${TAG}, [data-spectrum-snippet-id="${SNIPPET_ID}"]`))) return true
      }
    }
    return false
  }
  const __saiMo = new MutationObserver((records) => {
    if (__saiMo._t || !__saiRelevant(records)) return
    __saiMo._t = setTimeout(() => { __saiMo._t = null; bindAll() }, 150)
  })
  __saiMo.observe(document.documentElement, { childList: true, subtree: true })
  setTimeout(function () { try { __saiMo.disconnect() } catch (e) {} }, 30000)
})()
